import { Card, CardHeader, CardBody } from '@heroui/react'
import { Activity, Edit, Plus, Trash2, Copy } from 'lucide-react'
import type { AnalyticsSummary } from '../../types'

interface ActivityTimelineProps {
  data: AnalyticsSummary
}

function getActionIcon(action: string) {
  const lowerAction = action.toLowerCase()
  if (lowerAction.includes('create')) return <Plus className="w-4 h-4" />
  if (lowerAction.includes('update') || lowerAction.includes('edit')) return <Edit className="w-4 h-4" />
  if (lowerAction.includes('delete')) return <Trash2 className="w-4 h-4" />
  if (lowerAction.includes('duplicate') || lowerAction.includes('clone')) return <Copy className="w-4 h-4" />
  return <Activity className="w-4 h-4" />
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}

export function ActivityTimeline({ data }: ActivityTimelineProps) {
  const hasActivity = data.recentActivity.length > 0

  if (!hasActivity) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Recent Activity</h3>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-tf-text-muted mb-2">
              <Activity className="w-16 h-16 mx-auto" strokeWidth={1.5} />
            </div>
            <p className="text-tf-text-muted font-medium">No recent activity</p>
            <p className="text-sm text-tf-text-muted mt-1">
              Activity will appear as you work with funnels
            </p>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Recent Activity</h3>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          {data.recentActivity.map((activity: { date: string; action: string; funnelName: string }, index: number) => (
            <div key={index} className="flex items-start gap-3 pb-4 border-b border-tf-border-sub last:border-0 last:pb-0">
              <div className="flex-shrink-0 p-2 rounded-full bg-tf-bg-hover text-tf-text-muted">
                {getActionIcon(activity.action)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-tf-text">{activity.action}</p>
                <p className="text-sm text-tf-text-muted truncate">{activity.funnelName}</p>
              </div>
              <div className="flex-shrink-0">
                <span className="text-xs text-tf-text-muted">{formatDate(activity.date)}</span>
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}
